import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpeedIcon from '@mui/icons-material/Speed';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { formatTry } from '../../utils/reportRevenueUtils';
import { BRAND } from '../../theme/brandTheme';

/** CRO command center KPI row — win rate, avg uplift, velocity, brand leaderboard */
const CroKpiCards = ({ croKpis, user, hideLeaderboard = false }) => {
  if (!croKpis) return null;

  const { winRate, avgUplift, testVelocity, brandLeaderboard = [], portfolioYearlyRevenue = 0 } =
    croKpis;
  const isAdmin = user?.role === 'admin';

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.purple, mb: 2 }}>
        CRO Özet
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: BRAND.green }}>
                    %{winRate}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Win Rate
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Kazanan test / sonuçlanan test
                  </Typography>
                </Box>
                <EmojiEventsIcon sx={{ fontSize: 40, color: '#5ADC87' }} />
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min(winRate, 100)}
                sx={{
                  mt: 2,
                  height: 8,
                  borderRadius: 1,
                  bgcolor: 'rgba(0,0,0,0.08)',
                  '& .MuiLinearProgress-bar': { bgcolor: BRAND.green },
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: BRAND.purple }}>
                    +{avgUplift}%
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Ort. Uplift
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Kazanan testlerde ortalama iyileşme
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, color: '#6588DF' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#E57702' }}>
                    {testVelocity?.created ?? 0}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Test Hızı (30 gün)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {testVelocity?.created ?? 0} yeni · {testVelocity?.completed ?? 0} tamamlanan
                  </Typography>
                </Box>
                <SpeedIcon sx={{ fontSize: 40, color: '#F7BE64' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: BRAND.green }}>
                    {formatTry(portfolioYearlyRevenue)}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Portföy Geliri / Yıl
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Kazanan testler · AOV tanımlı
                  </Typography>
                </Box>
                <AttachMoneyIcon sx={{ fontSize: 40, color: '#5ADC87' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Brand leaderboard — show when multiple brands or admin */}
      {!hideLeaderboard && brandLeaderboard.length > 0 && (isAdmin || brandLeaderboard.length > 1) && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Marka Leaderboard
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Marka</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Test
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Win Rate
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Kazanan
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {brandLeaderboard.slice(0, 8).map((row) => (
                    <TableRow key={row.brandId}>
                      <TableCell>{row.brandName}</TableCell>
                      <TableCell align="right">{row.testCount}</TableCell>
                      <TableCell align="right">
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: 600,
                            color: row.winRate >= 50 ? BRAND.green : 'text.primary',
                          }}
                        >
                          %{row.winRate}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {row.wins}/{row.testsWithResult ?? row.testCount}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default CroKpiCards;
